import { useQuery } from "@tanstack/react-query";
import { getCategories, getThemeSettings, getBrandsConfig } from "./api";
import api from "./api";
import { CACHE_KEYS, STALE_TIMES, getLocalCache, setLocalCache } from "./queryClient";

// Categories – static data, cached aggressively
export function useCategories(activeOnly = false) {
    return useQuery({
        queryKey: activeOnly ? CACHE_KEYS.CATEGORIES_ACTIVE : CACHE_KEYS.CATEGORIES,
        queryFn: async () => {
            const cacheKey = activeOnly ? "categories_active" : "categories";
            const cached = getLocalCache(cacheKey);
            if (cached) return cached;
            const r = await getCategories(activeOnly);
            const data = r.data || [];
            setLocalCache(cacheKey, data);
            return data;
        },
        staleTime: STALE_TIMES.STATIC,
        gcTime: 30 * 60 * 1000,
    });
}

// Theme settings – very static
export function useThemeSettings() {
    return useQuery({
        queryKey: CACHE_KEYS.THEME,
        queryFn: async () => {
            const cached = getLocalCache("theme");
            if (cached) return cached;
            const r = await getThemeSettings();
            const data = r.data || {};
            setLocalCache("theme", data);
            return data;
        },
        staleTime: STALE_TIMES.STATIC,
        gcTime: 30 * 60 * 1000,
    });
}

// Category brands – semi-static, per category
export function useCategoryBrands(slug) {
    return useQuery({
        queryKey: [CACHE_KEYS.CATEGORY_BRANDS, slug],
        queryFn: async () => {
            if (!slug) {
                const r = await api.get("/products/autocomplete/brands");
                return (r.data || []).map(b => typeof b === "string" ? b : (b?.name || "")).filter(Boolean);
            }
            const r = await api.get(`/categories/${slug}/brands`);
            return (r.data || []).map(b => typeof b === "string" ? b : (b?.name || "")).filter(Boolean);
        },
        staleTime: STALE_TIMES.SEMI_STATIC,
        enabled: true,
    });
}

// Product parameter values – semi-static
export function useParamValues(categorySlug) {
    return useQuery({
        queryKey: [CACHE_KEYS.PARAM_VALUES, categorySlug || "all"],
        queryFn: async () => {
            const params = categorySlug ? { category_slug: categorySlug } : {};
            const r = await api.get("/products/parameter-values", { params });
            return r.data || [];
        },
        staleTime: STALE_TIMES.SEMI_STATIC,
    });
}

// Brand configs with logos – static
export function useBrandConfigs() {
    return useQuery({
        queryKey: CACHE_KEYS.BRAND_CONFIGS,
        queryFn: async () => {
            const cached = getLocalCache("brand_configs");
            if (cached) return cached;
            const r = await getBrandsConfig();
            const data = r.data || [];
            setLocalCache("brand_configs", data);
            return data;
        },
        staleTime: STALE_TIMES.STATIC,
        gcTime: 30 * 60 * 1000,
    });
}

// Bestsellers – semi-static
export function useBestsellers(slug, limit = 4) {
    return useQuery({
        queryKey: [CACHE_KEYS.BESTSELLERS, slug || "all", limit],
        queryFn: async () => {
            const params = slug ? { category_slug: slug, limit } : { limit };
            const r = await api.get("/products/bestsellers", { params });
            return r.data?.items || [];
        },
        staleTime: STALE_TIMES.SEMI_STATIC,
    });
}
